import { useCallback, useMemo, useState } from "react";
import {
	useDeleteDraftOrderMutation,
	useDeleteTemplateOrderMutation,
	useLazyGetDraftOrdersQuery,
	useLazyGetQuotesQuery,
	useLazyGetTemplatesQuery
} from "features/api/orderApi.ts";
import { DraftType, DraftTypeType } from "types/declarations/enums.ts";
import { models } from "types/api/viewModels.ts";

const EMPTY_DRAFTS: models["DraftOrderViewModel"][] = [];

export const useDrafts = () => {
	const [draftTrigger, { data: drafts, error: draftError, isFetching: isDraftFetching }] =
		useLazyGetDraftOrdersQuery();
	const [templateTrigger, { data: templates, error: templateError, isFetching: isTemplateFetching }] =
		useLazyGetTemplatesQuery();
	const [quotesTrigger, { data: quotes, isFetching: isQuoteFetching }] = useLazyGetQuotesQuery();

	const [
		deleteTemplate,
		{
			isLoading: deleteTemplateLoading,
			isSuccess: deleteTemplateSuccess,
			error: deleteTemplateError,
			reset: resetDeleteTemplate
		}
	] = useDeleteTemplateOrderMutation();
	const [
		deleteDraft,
		{
			isLoading: deleteDraftLoading,
			isSuccess: deleteDraftSuccess,
			error: deleteDraftError,
			reset: resetDeleteDraft
		}
	] = useDeleteDraftOrderMutation();

	const [draftViewType, setDraftViewType] = useState<DraftTypeType>(DraftType.Draft);

	const config = useMemo(() => {
		return {
			[DraftType.Draft]: {
				data: drafts,
				error: draftError,
				trigger: draftTrigger,
				delete: deleteDraft,
				deleteSuccess: deleteDraftSuccess,
				deleteError: deleteDraftError,
				deleteLoading: deleteDraftLoading,
				reset: resetDeleteDraft
			},
			[DraftType.Template]: {
				data: templates,
				error: templateError,
				trigger: templateTrigger,
				delete: deleteTemplate,
				deleteSuccess: deleteTemplateSuccess,
				deleteError: deleteTemplateError,
				deleteLoading: deleteTemplateLoading,
				reset: resetDeleteTemplate
			},
			[DraftType.Quote]: {
				data: quotes,
				error: undefined,
				trigger: quotesTrigger,
				delete: deleteDraft,
				deleteSuccess: deleteDraftSuccess,
				deleteError: deleteDraftError,
				deleteLoading: deleteDraftLoading,
				reset: resetDeleteDraft
			}
		};
	}, [
		deleteDraft,
		deleteDraftError,
		deleteDraftLoading,
		deleteDraftSuccess,
		deleteTemplate,
		deleteTemplateError,
		deleteTemplateLoading,
		deleteTemplateSuccess,
		draftError,
		draftTrigger,
		drafts,
		quotes,
		quotesTrigger,
		resetDeleteDraft,
		resetDeleteTemplate,
		templateError,
		templateTrigger,
		templates
	]);

	const active = config[draftViewType];

	const getDraftsTrigger = useCallback(
		(manualType?: DraftTypeType) => {
			const resolvedType = manualType ?? draftViewType;
			return config[resolvedType]?.trigger({});
		},
		[draftViewType, config]
	);

	return {
		allDrafts: active?.data ?? EMPTY_DRAFTS,
		getDraftsTrigger,
		getDraftsError: active?.error ?? null,
		deleteSelectedDraft: active?.delete,
		deleteSuccess: active?.deleteSuccess ?? false,
		deleteError: active?.deleteError ?? null,
		deleteLoading: active?.deleteLoading ?? false,
		isFetchingDrafts: isDraftFetching || isTemplateFetching || isQuoteFetching,
		resetDeleteSelectedDraft: active?.reset,
		setDraftViewType,
		draftViewType
	};
};
